import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

export type SearchedOption = { value: string; label: string; keywords?: string[] }

/** One page of matches, and the offset of the page after it (null on the last). */
export type SearchedOptionsPage = { options: SearchedOption[]; nextOffset: number | null }

/** How long the search box has to rest before its text is sent. */
const SEARCH_DEBOUNCE_MS = 250

type UseSearchedOptionsParams = {
  /** Prefix of every query key this hook issues, so an invalidation reaches all of its pages. */
  queryKey: string
  /**
   * Anything else the list depends on besides the search (a year, a type). Part of every key, so
   * a change refetches from the first page; `fetchMatches` reads the same value from its closure.
   */
  scope?: unknown
  /** The values currently selected; each is always offered, so it can be seen and unselected. */
  selected: string[]
  /**
   * The options containing `search` from `offset` on — one server page, ordered as it should be
   * shown, with the offset of the next page or null when this is the last.
   */
  fetchMatches: (
    search: string,
    offset: number,
    signal?: AbortSignal,
  ) => Promise<SearchedOptionsPage>
  /**
   * The options for selected values no fetch has shown yet — seeded from a URL or a saved draft —
   * so they carry a proper label. Omit when a value is its own label.
   */
  fetchSelected?: (values: string[], signal?: AbortSignal) => Promise<SearchedOption[]>
  /** The option shown for a selected value while, or if never, resolved. Defaults to the value itself. */
  toFallbackOption?: (value: string) => SearchedOption
}

const valueAsOption = (value: string): SearchedOption => ({ value, label: value })

/**
 * Options for a dropdown whose list is fetched from the server as the user types, instead of
 * downloaded whole up front. For lists with one entry per customer (ERP, legal name, Equifax number,
 * D-U-N-S) the whole list is the customer base: a payload Sentry flags and a dropdown nobody scrolls.
 *
 * Pass `onSearchChange`, `isSearching`, `onEndReached`, `hasMoreOptions` and `isLoadingMore` to the
 * dropdown. The options offered are the selected values first, then the server's matches for the
 * current search, page after page as the user scrolls to the end of the list (its first page before
 * anything is typed). Every option ever fetched is remembered for the life of the component, so a
 * value picked under one search keeps its label under the next.
 */
export function useSearchedOptions({
  queryKey,
  scope,
  selected,
  fetchMatches,
  fetchSelected,
  toFallbackOption = valueAsOption,
}: UseSearchedOptionsParams) {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS)
  const [known, setKnown] = useState<ReadonlyMap<string, SearchedOption>>(() => new Map())

  const matches = useInfiniteQuery({
    queryKey: [queryKey, 'search', scope, debouncedSearch],
    queryFn: ({ pageParam, signal }) => fetchMatches(debouncedSearch, pageParam, signal),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
    // The previous search's pages stay on screen while the next loads, instead of flashing empty.
    placeholderData: keepPreviousData,
  })
  // Left possibly undefined: a fresh `[]` each render would re-run the effect below every time.
  const fetchedPages = matches.data?.pages

  // Once resolved a value is known, so the key empties and the query goes idle — no refetch.
  const unresolved = fetchSelected ? selected.filter((value) => !known.has(value)) : []
  const resolved = useQuery({
    queryKey: [queryKey, 'selected', scope, unresolved],
    queryFn: ({ signal }) => fetchSelected!(unresolved, signal),
    enabled: unresolved.length > 0,
  })

  useEffect(() => {
    const fetched = [
      ...(resolved.data ?? []),
      ...(fetchedPages ?? []).flatMap((page) => page.options),
    ]
    setKnown((previous) => {
      const isNew = fetched.some((option) => previous.get(option.value) !== option)
      if (!isNew) return previous

      const next = new Map(previous)
      for (const option of fetched) next.set(option.value, option)
      return next
    })
  }, [fetchedPages, resolved.data])

  // Selected first; then every page's matches, once each — a server that carries the selection
  // along with every page (the ERP endpoint) hands the same row back more than once.
  const seen = new Set(selected)
  const options: SearchedOption[] = selected.map(
    (value) => known.get(value) ?? toFallbackOption(value),
  )
  for (const page of fetchedPages ?? []) {
    for (const option of page.options) {
      if (seen.has(option.value)) continue
      seen.add(option.value)
      options.push(option)
    }
  }

  // True from the first keystroke until the server has answered for that text: the dropdown shows
  // its empty list as loading, not as "no results", while the typed text outruns the list.
  const isSearching =
    search.trim() !== debouncedSearch || (matches.isFetching && !matches.isFetchingNextPage)

  const onEndReached = () => {
    if (matches.hasNextPage && !matches.isFetching) void matches.fetchNextPage()
  }

  return {
    options,
    isPending: matches.isPending,
    isSearching,
    onSearchChange: setSearch,
    /** Asks for the next page of the current search; a no-op on the last page or mid-fetch. */
    onEndReached,
    hasMoreOptions: matches.hasNextPage,
    isLoadingMore: matches.isFetchingNextPage,
  }
}
